import React, { useEffect, useState } from 'react';
import { Bell, MapPin, X, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { requestFcmToken, refreshFcmToken } from '../firebase';
import { sendTokenToBackend } from '../api/notifications';

const DISMISS_KEY = 'homphy_notif_banner_dismissed';
const GEO_ASKED_KEY = 'homphy_geo_asked';
const BANNER_HEIGHT = 56;

type GeoState = 'granted' | 'prompt' | 'denied' | 'unknown';

/**
 * Top-of-page prompt asking logged-in users to turn on push notifications and location.
 * Also keeps the device token saved per account (the old single cache key
 * skipped the backend save when a different account used the same browser).
 */
export default function NotificationPrompt() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const supported = typeof Notification !== 'undefined';
  const hasGeo = typeof navigator !== 'undefined' && !!navigator.geolocation;
  const [perm, setPerm] = useState<NotificationPermission>(supported ? Notification.permission : 'denied');
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [syncFailed, setSyncFailed] = useState(false);
  const [geo, setGeo] = useState<GeoState>('unknown');
  const [geoAsked, setGeoAsked] = useState(() => {
    try { return localStorage.getItem(GEO_ASKED_KEY) === '1'; } catch { return false; }
  });
  const [geoBusy, setGeoBusy] = useState(false);

  const cacheKey = user ? `homphy_fcm_user_${user.id}` : '';

  // Permission already granted: make sure THIS account has the token saved on the backend.
  useEffect(() => {
    if (!user || !supported || perm !== 'granted') return;
    let cancelled = false;
    (async () => {
      try {
        const token = await refreshFcmToken();
        if (cancelled) return;
        if (!token) { setSyncFailed(true); return; }
        setSyncFailed(false);
        if (localStorage.getItem(cacheKey) === token) return;
        await sendTokenToBackend(token);
        localStorage.setItem(cacheKey, token);
      } catch {
        if (!cancelled) setSyncFailed(true);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id, perm]);

  // Location permission state, where the browser supports checking it
  useEffect(() => {
    if (!hasGeo || !navigator.permissions || !navigator.permissions.query) return undefined;
    const holder: { status: PermissionStatus | null } = { status: null };
    let cancelled = false;
    const onChange = () => { if (holder.status) setGeo(holder.status.state as GeoState); };
    navigator.permissions.query({ name: 'geolocation' as PermissionName }).then((s) => {
      if (cancelled) return;
      holder.status = s;
      setGeo(s.state as GeoState);
      s.addEventListener('change', onChange);
    }).catch(() => { /* not supported */ });
    return () => {
      cancelled = true;
      if (holder.status) holder.status.removeEventListener('change', onChange);
    };
  }, []);

  const notifNeeded = !!user && supported && !done && (perm === 'default' || perm === 'denied' || syncFailed);
  const geoNeeded = hasGeo && (geo === 'prompt' || (geo === 'unknown' && !geoAsked));
  const visible = !dismissed && (notifNeeded || geoNeeded);

  // Push page content down so the banner never hides it
  useEffect(() => {
    if (!visible) return;
    const prev = document.body.style.paddingTop;
    document.body.style.paddingTop = `calc(${BANNER_HEIGHT}px + env(safe-area-inset-top, 0px))`;
    return () => { document.body.style.paddingTop = prev; };
  }, [visible]);

  const enable = async () => {
    setBusy(true);
    setError('');
    try {
      const result = await Notification.requestPermission();
      setPerm(result);
      if (result !== 'granted') {
        setError(t('Notifications are blocked. Allow them in your browser site settings, then reload.', 'Arifa zimezuiwa. Ziruhusu kwenye mipangilio ya tovuti ya kivinjari, kisha pakia upya.'));
        return;
      }
      const token = await requestFcmToken();
      if (!token) throw new Error('no token');
      await sendTokenToBackend(token);
      localStorage.setItem(cacheKey, token);
      setSyncFailed(false);
      setDone(true);
    } catch {
      setError(t('Could not turn on notifications. Reload the page and try again.', 'Imeshindwa kuwasha arifa. Pakia upya ukurasa ujaribu tena.'));
    } finally {
      setBusy(false);
    }
  };

  const markGeoAsked = () => {
    setGeoAsked(true);
    try { localStorage.setItem(GEO_ASKED_KEY, '1'); } catch { /* ignore */ }
  };

  const enableLocation = () => {
    if (!hasGeo) return;
    setGeoBusy(true);
    setError('');
    navigator.geolocation.getCurrentPosition(
      () => { setGeoBusy(false); setGeo('granted'); markGeoAsked(); },
      () => {
        setGeoBusy(false);
        markGeoAsked();
        setGeo('denied');
        setError(t('Location is blocked. Allow it in your browser site settings, then reload.', 'Eneo limezuiwa. Liruhusu kwenye mipangilio ya tovuti ya kivinjari, kisha pakia upya.'));
      },
      { timeout: 15000 }
    );
  };

  const dismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
  };

  if (!visible) return null;

  const message = error
    ? error
    : notifNeeded
      ? (perm === 'denied'
          ? t('Notifications are blocked. Allow them in your browser site settings to get booking updates.', 'Arifa zimezuiwa. Ziruhusu kwenye mipangilio ya tovuti ili upate taarifa za miadi.')
          : t('Turn on notifications to get booking updates. Important, so you never miss a request.', 'Washa arifa upate taarifa za miadi. Muhimu, ili usikose ombi lolote.'))
      : t('Homphy needs your location to match you with the nearest provider. Turn it on so we can find care close to you.', 'Homphy inahitaji eneo lako ili kukulinganisha na mtoa huduma wa karibu. Liwashe ili tukupatie huduma karibu nawe.');

  const pill: React.CSSProperties = {
    flexShrink: 0, background: '#fff', color: '#0a5e40', border: 'none', borderRadius: 100,
    padding: '7px 16px', fontSize: '0.78rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: 6,
  };

  return (
    <div
      role="status"
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
        paddingTop: 'env(safe-area-inset-top, 0px)',
        background: 'linear-gradient(135deg, #1D9E75, #0a5e40)', color: '#fff',
        boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
      }}
    >
      <div style={{ minHeight: BANNER_HEIGHT, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, padding: '8px 12px', maxWidth: 1180, margin: '0 auto' }}>
        <Bell size={18} style={{ flexShrink: 0 }} />
        <span style={{ flex: '1 1 220px', fontSize: '0.8rem', fontWeight: 600, lineHeight: 1.35 }}>{message}</span>
        {notifNeeded && perm !== 'denied' && (
          <button onClick={enable} disabled={busy} style={{ ...pill, cursor: busy ? 'wait' : 'pointer' }}>
            {busy && <Loader2 size={13} className="animate-spin" />}
            {syncFailed ? t('Retry', 'Jaribu tena') : t('Enable', 'Washa')}
          </button>
        )}
        {geoNeeded && (
          <button onClick={enableLocation} disabled={geoBusy} style={{ ...pill, cursor: geoBusy ? 'wait' : 'pointer' }}>
            {geoBusy ? <Loader2 size={13} className="animate-spin" /> : <MapPin size={13} />}
            {t('Turn on location', 'Washa eneo')}
          </button>
        )}
        <button onClick={dismiss} aria-label="Dismiss" style={{ flexShrink: 0, background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', padding: 4, display: 'flex' }}>
          <X size={16} />
        </button>
      </div>
    </div>
  );
}